import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Warehouse, Save, CheckCircle2, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import AdminTopBar from "@/components/AdminTopBar";

const EMPTY = {
  client_name: "", pickup_name: "", phone: "", email: "",
  address: "", city: "", state: "", pincode: "",
};

const AdminSettings = () => {
  const { token, isAdmin } = useAdmin();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [meta, setMeta] = useState({ token_configured: false, registered: false });

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isAdmin) return;
    http.get("/admin/settings/shipping", auth)
      .then((r) => {
        const { token_configured, registered, ...rest } = r.data;
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(rest).filter(([k]) => k in EMPTY)) });
        setMeta({ token_configured, registered });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full h-10 border border-[#e2e8f0] rounded-sm px-3 text-sm outline-none focus:border-[#C62828] bg-white";

  const save = async (e) => {
    e.preventDefault();
    const required = ["client_name", "pickup_name", "phone", "address", "city", "state", "pincode"];
    for (const k of required) if (!form[k].trim()) return toast.error("Please fill all required fields");
    if (form.pincode.replace(/\D/g, "").length !== 6) return toast.error("Enter a valid 6-digit pincode");
    setSaving(true);
    try {
      await http.put("/admin/settings/shipping", form, auth);
      toast.success("Pickup address saved — paid orders will now auto-manifest");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const registerWarehouse = async () => {
    setRegistering(true);
    try {
      await http.post("/admin/settings/shipping/register-warehouse", {}, auth);
      setMeta((m) => ({ ...m, registered: true }));
      toast.success("Pickup warehouse registered with Delhivery");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setRegistering(false);
    }
  };

  return (
    <main className="min-h-[80vh] bg-[#f8fafc]" data-testid="admin-settings-page">
      <AdminTopBar />
      <div className="max-w-[760px] mx-auto px-4 py-6">
        <div className="bg-white rounded-sm border border-[#e2e8f0] p-5">
          <div className="flex items-center gap-2 mb-1">
            <Warehouse className="w-5 h-5 text-[#C62828]" />
            <h1 className="font-head text-lg font-bold text-[#0f172a]">Pickup / Warehouse Address</h1>
          </div>
          <p className="text-sm text-[#64748b] mb-4">
            This is where Delhivery picks up parcels. Once saved, every paid order is auto-manifested from here.
          </p>

          <div className={`flex items-center gap-2 text-sm rounded-sm px-3 py-2 mb-4 ${
            meta.token_configured ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fef9c3] text-[#a16207]"
          }`}>
            {meta.token_configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            Delhivery API token {meta.token_configured ? "is configured" : "is not configured"}
          </div>

          {loading ? (
            <p className="text-[#64748b] py-8 text-center">Loading…</p>
          ) : (
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748b]">Registered Client Name *</label>
                <input value={form.client_name} onChange={f("client_name")} className={inputCls} placeholder="As registered with Delhivery" data-testid="settings-client-name" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Pickup / Warehouse Name *</label>
                <input value={form.pickup_name} onChange={f("pickup_name")} className={inputCls} placeholder="Exact registered pickup name" data-testid="settings-pickup-name" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Contact Phone *</label>
                <input value={form.phone} onChange={f("phone")} inputMode="numeric" className={inputCls} placeholder="10-digit phone" data-testid="settings-phone" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Email</label>
                <input value={form.email} onChange={f("email")} className={inputCls} placeholder="warehouse@bharaticrafts.com" data-testid="settings-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#64748b]">Address *</label>
                <input value={form.address} onChange={f("address")} className={inputCls} placeholder="Building, street, area" data-testid="settings-address" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">City *</label>
                <input value={form.city} onChange={f("city")} className={inputCls} data-testid="settings-city" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">State *</label>
                <input value={form.state} onChange={f("state")} className={inputCls} data-testid="settings-state" />
              </div>
              <div>
                <label className="text-xs text-[#64748b]">Pincode *</label>
                <input value={form.pincode} onChange={f("pincode")} inputMode="numeric" maxLength={6} className={inputCls} data-testid="settings-pincode" />
              </div>

              <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-2">
                <button type="submit" disabled={saving} className="flex items-center gap-1.5 bg-[#C62828] text-white text-sm font-semibold px-5 h-10 rounded-sm hover:bg-[#A01F1F] disabled:opacity-60" data-testid="settings-save-btn">
                  <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Pickup Address"}
                </button>
                <button type="button" onClick={registerWarehouse} disabled={registering || !meta.token_configured} className="flex items-center gap-1.5 border border-[#7e22ce]/40 text-[#7e22ce] text-sm font-semibold px-5 h-10 rounded-sm hover:bg-purple-50 disabled:opacity-60" data-testid="settings-register-btn">
                  <Send className="w-4 h-4" /> {registering ? "Registering…" : "Register with Delhivery"}
                </button>
                {meta.registered && (
                  <span className="flex items-center gap-1 text-sm text-[#15803d] font-medium" data-testid="settings-registered-badge">
                    <CheckCircle2 className="w-4 h-4" /> Registered
                  </span>
                )}
              </div>
              <p className="sm:col-span-2 text-xs text-[#94a3b8]">
                Save first, then register the warehouse with Delhivery (one-time). The pickup name must exactly match what Delhivery has on file.
              </p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default AdminSettings;
